import { useEffect, useRef, useState } from 'react';

// Enough warning that the row below the fold is already working by the time it
// arrives, and not so much that landing on a page counts everything below it as
// near. Measured on the educators home page, whose card grid starts 273px under
// the fold: at 600px the whole first screen of cards was "near" on arrival and
// the gate bought nothing, while at 200px landing costs nothing and the smallest
// scroll toward the grid starts it.
const DEFAULT_ROOT_MARGIN = '200px';

/**
 * The scrolling ancestor to measure against, or null for the viewport.
 *
 * Needed because `rootMargin` inflates the ROOT's rectangle and nothing else:
 * the intersection is still clipped by every scrolling ancestor in between, and
 * those get no margin. This app scrolls inside `.main-panel` rather than the
 * document — body is `overflow: hidden` — so an observer left on the default
 * root reports a card as outside until it is genuinely on screen, and the
 * look-ahead silently does nothing.
 *
 * Overflow alone decides it, not `scrollHeight > clientHeight`: the container is
 * the scroller whether or not it happens to be full at the moment this runs.
 */
const scrollParentOf = (node) => {
  let parent = node.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
};

/**
 * A ref to attach, plus whether that element has come within `rootMargin` of the
 * viewport at least once. For work that should not start until the user is
 * nearly looking at it.
 *
 * LATCHES ON: once true it stays true and the observer is dropped. Scrolling
 * past must not turn it back off — a caller that keys expensive work on this
 * would tear that work down and pay for it again on the way back, which is worse
 * than never having gated it.
 *
 * Without IntersectionObserver it reports true immediately, so an old browser
 * sees everything rather than nothing.
 *
 * @returns {[React.RefObject, boolean]}
 */
const useNearViewport = (rootMargin = DEFAULT_ROOT_MARGIN) => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (near) return undefined;

    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setNear(true);
      },
      { root: scrollParentOf(node), rootMargin },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [near, rootMargin]);

  return [ref, near];
};

export default useNearViewport;
